import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import {
  BodyText,
  Box,
  DateTimePicker,
  FormField,
  Input,
  RadioButton,
  SkeletonLoader,
  TextArea,
} from '@chargify/custom-ui-components';
import {
  AutomaticResumeOption,
  OptionsWrapper,
} from '../../../../components/RadioOptions';
import { CancelOptions } from '../../constants';
import { formatDate } from '@chargify/formatters';
import { InputsWrapper, Separator } from '../Content/Content.styles';
import ChurnReasonCodes from '../ChurnResonCodes';
import { ChurnReason } from '../../../../models';
import { Context } from '../../machines/types';

export type FormProps = {
  modalRef: RefObject<HTMLElement>;
  portalContainer: RefObject<HTMLElement>;
  onChange: (name: keyof Context, value: string | ChurnReason[]) => void;
  /**
   * Available cancel options
   */
  availableCancelOptions: CancelOptions[];
  subscriptionNextAssessmentDate: string;
  values: Context;
  isLoading: boolean;
};
export const Form = ({
  onChange,
  availableCancelOptions,
  values,
  modalRef,
  portalContainer,
  subscriptionNextAssessmentDate,
  isLoading,
}: FormProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const {
    cancellationType,
    cancellationChurnReasonCode,
    cancellationDate,
    churnReasons,
  } = values;

  return (
    <>
      <Box marginBottom={3}>
        {availableCancelOptions.length > 1 && (
          <>
            <Box marginBottom={5}>
              <BodyText>{t('cancel_subscription.cancel_description')}</BodyText>
            </Box>
            <OptionsWrapper>
              {availableCancelOptions.includes(
                CancelOptions.IMMEDIATE_CANCEL
              ) && (
                <RadioButton
                  label={t('cancel_subscription.cancel_immediately')}
                  value={CancelOptions.IMMEDIATE_CANCEL}
                  onValueChange={(value: string) =>
                    onChange('cancellationType', value)
                  }
                  isSelected={
                    cancellationType === CancelOptions.IMMEDIATE_CANCEL
                  }
                />
              )}
              {availableCancelOptions.includes(
                CancelOptions.DELAYED_CANCEL
              ) && (
                <RadioButton
                  label={t(
                    'cancel_subscription.cancel_by_the_end_of_the_billing_period',
                    { date: formatDate(subscriptionNextAssessmentDate) }
                  )}
                  value={CancelOptions.DELAYED_CANCEL}
                  onValueChange={(value: string) =>
                    onChange('cancellationType', value)
                  }
                  isSelected={cancellationType === CancelOptions.DELAYED_CANCEL}
                />
              )}
              {availableCancelOptions.includes(
                CancelOptions.SCHEDULED_CANCEL
              ) && (
                <AutomaticResumeOption>
                  <RadioButton
                    label={t('cancel_subscription.cancel_on_selected_date')}
                    value={CancelOptions.SCHEDULED_CANCEL}
                    onValueChange={(value: string) =>
                      onChange('cancellationType', value)
                    }
                    isSelected={
                      cancellationType === CancelOptions.SCHEDULED_CANCEL
                    }
                  />
                  <DateTimePicker
                    onChange={(dateTime: Dayjs) =>
                      onChange('cancellationDate', dateTime.toISOString())
                    }
                    modalRef={modalRef}
                    portalContainer={portalContainer}
                    initialDate={cancellationDate}
                    minDate={dayjs().add(1, 'day').toDate()}
                    disabled={
                      cancellationType !== CancelOptions.SCHEDULED_CANCEL
                    }
                  />
                </AutomaticResumeOption>
              )}
            </OptionsWrapper>
            <Separator />
          </>
        )}
        <InputsWrapper>
          {values.churnReasons?.length > 0 && (
            <ChurnReasonCodes
              selectedValue={cancellationChurnReasonCode}
              onChange={(value) =>
                onChange('cancellationChurnReasonCode', value)
              }
              items={churnReasons}
            />
          )}
          {isLoading && (
            <Box marginBottom={6}>
              <SkeletonLoader height={100} width={350} />
            </Box>
          )}
          <FormField
            label={t('cancel_subscription.feedback_title')}
            labelPosition="top"
            name="feedback"
            size="medium"
          >
            <TextArea
              name="feedback"
              placeholder={t('cancel_subscription.feedback_placeholder')}
              onChange={(e) => {
                onChange('cancellationMessage', e.target.value);
              }}
              rows={2}
            />
          </FormField>
          <FormField
            label={t('cancel_subscription.confirmation_title')}
            labelPosition="top"
            name="input-name"
            size="medium"
            required
          >
            <Input
              type="text"
              placeholder={t('cancel_subscription.confirmation_placeholder')}
              onChange={(e) =>
                onChange('cancellationConfirmation', e.target.value)
              }
            />
          </FormField>
        </InputsWrapper>
      </Box>
    </>
  );
};
